import uid from "common/utils/uid";
import type { PrimaryEntityId } from "../doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "../entities/docEntityBase";
import { docEntityBaseNew } from "../entities/docEntityBaseNew";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { WallPlaneOperationBoundary } from "./wallPlane";
import type { DocBuildingGraphFilterGraph, DocFaceDirection, DocFaceNodeSubFilter } from "./wallPlaneFilters";
import { makeEmptyDocBuildingGraphFilterGraph } from "./wallPlaneFilters";
import type { WallPlaneOperation } from "./wallPlaneOperations";

export type WallPlaneRuleKind = "WALL_FACE" | "WALL_PLANE";

interface WallPlaneRuleBase extends FormulaDisabledDocEntityBase<"WALL_PLANE_RULE"> {
  readonly id: PrimaryEntityId<"WALL_PLANE_RULE">;
  readonly ruleKind: WallPlaneRuleKind;
  readonly operation?: WallPlaneOperation;
  readonly operationBoundary?: WallPlaneOperationBoundary;
}

/** Face-node rule: optional catalog filter + optional instance-specific narrowing. */
export interface WallFaceRuleEntity extends WallPlaneRuleBase {
  readonly ruleKind: "WALL_FACE";
  readonly filterId?: ForeignEntityId<"WALL_PLANE_FILTER">;
  readonly instanceFilterGraphId: string;
  readonly instanceFilterGraphs: Record<string, DocBuildingGraphFilterGraph>;
  /**
   * Optional projection sub-filter (UNDEFINED / PERIMETER / OPPOSITE). When set and not
   * UNDEFINED, overrides the linked catalog filter's subFilter for this rule.
   * OPPOSITE graphs live in {@link instanceFilterGraphs}.
   */
  readonly subFilter?: DocFaceNodeSubFilter;
  /** Which side of matched face nodes this rule's operator applies to. */
  readonly direction: DocFaceDirection;

  /** Optional ID tying the rule to a roof, allowing for cascading delete of rules associated with a roof */
  readonly roofId?: ForeignEntityId<"ROOF">;
}

/** Plane-anchored rule: targets one wall plane by hash. No catalog filter or subFilter. */
export interface WallPlaneAnchoredRuleEntity extends WallPlaneRuleBase {
  readonly ruleKind: "WALL_PLANE";
  readonly planeHash: string;
  readonly direction: DocFaceDirection;
}

export type WallPlaneRuleEntity = WallFaceRuleEntity | WallPlaneAnchoredRuleEntity;

export function isWallFaceRule(rule: WallPlaneRuleEntity): rule is WallFaceRuleEntity {
  if (rule.ruleKind === "WALL_FACE") {
    return true;
  }
  if (rule.ruleKind === "WALL_PLANE") {
    return false;
  }
  return !("planeHash" in rule);
}

export function isWallPlaneAnchoredRule(rule: WallPlaneRuleEntity): rule is WallPlaneAnchoredRuleEntity {
  if (rule.ruleKind === "WALL_PLANE") {
    return true;
  }
  if (rule.ruleKind === "WALL_FACE") {
    return false;
  }
  return "planeHash" in rule;
}

export function wallFaceRuleHasInstancePredicates(rule: WallFaceRuleEntity): boolean {
  if (!rule.instanceFilterGraphs || !rule.instanceFilterGraphId) {
    return false;
  }
  const graph = rule.instanceFilterGraphs[rule.instanceFilterGraphId];
  if (!graph) {
    return false;
  }
  return Object.values(graph.nodes).some((node) => node.filterType === "PREDICATE");
}

export function wallFaceRuleHasConstraint(rule: WallFaceRuleEntity): boolean {
  if (rule.filterId !== undefined) {
    return true;
  }
  if (wallFaceRuleHasInstancePredicates(rule)) {
    return true;
  }
  // PERIMETER / OPPOSITE alone are constraints: primary match is unconstrained
  // (all FACE_NODEs), then projection narrows via the sub-filter.
  return rule.subFilter !== undefined && rule.subFilter.kind !== "UNDEFINED";
}

export function wallFaceRuleNew({
  name = "Wall Face Rule",
  id = uid(),
  sortIndex = 0,
  filterId,
  instanceFilterGraphId,
  instanceFilterGraphs,
  subFilter = { kind: "UNDEFINED" },
  direction = "POSITIVE_NORMAL",
  operation,
  operationBoundary,
  disabled,
}: Partial<
  Pick<
    WallFaceRuleEntity,
    | "name"
    | "id"
    | "sortIndex"
    | "filterId"
    | "instanceFilterGraphId"
    | "instanceFilterGraphs"
    | "subFilter"
    | "direction"
    | "operation"
    | "operationBoundary"
    | "disabled"
  >
> = {}): WallFaceRuleEntity {
  const emptyInstance = makeEmptyDocBuildingGraphFilterGraph("FACE_NODE");
  return {
    ...docEntityBaseNew("WALL_PLANE_RULE", id),
    ruleKind: "WALL_FACE",
    name,
    sortIndex,
    filterId,
    instanceFilterGraphId: instanceFilterGraphId ?? emptyInstance.filterGraphId,
    instanceFilterGraphs: instanceFilterGraphs ?? emptyInstance.filterGraphs,
    subFilter,
    direction,
    operation,
    operationBoundary,
    disabled,
  };
}

export function wallPlaneAnchoredRuleNew({
  name = "Wall Plane Rule",
  id = uid(),
  sortIndex = 0,
  planeHash = "",
  direction = "POSITIVE_NORMAL",
  operation,
  operationBoundary,
  disabled,
}: Partial<
  Pick<
    WallPlaneAnchoredRuleEntity,
    "name" | "id" | "sortIndex" | "planeHash" | "direction" | "operation" | "operationBoundary" | "disabled"
  >
> = {}): WallPlaneAnchoredRuleEntity {
  return {
    ...docEntityBaseNew("WALL_PLANE_RULE", id),
    ruleKind: "WALL_PLANE",
    name,
    sortIndex,
    planeHash,
    direction,
    operation,
    operationBoundary,
    disabled,
  };
}

export function wallPlaneRuleLabelAdditional(rule: WallPlaneRuleEntity): string {
  const isFaceRule = rule.ruleKind === "WALL_FACE";
  let directionLabel = rule.direction === "POSITIVE_NORMAL" ? "Positive" : "Negative";
  if (isFaceRule) {
    directionLabel = rule.direction === "POSITIVE_NORMAL" ? "Outward" : "Inward";
  }
  return `${isFaceRule ? "Wall Face Rule" : "Wall Plane Rule"} - ${directionLabel}`;
}
